import { Search, FileSignature, Route, ClipboardCheck, Radio, Wallet } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { motion } from "framer-motion";

const services = [
  { icon: Search, title: "Load Sourcing", desc: "We hunt boards and direct-broker relationships for profitable, high-RPM loads that fit your truck and preferred lanes.", span: "md:col-span-6 md:row-span-2" },
  { icon: FileSignature, title: "Rate Negotiation", desc: "We push for every dollar. Track record beats book rates.", span: "md:col-span-3" },
  { icon: Route, title: "Route Planning", desc: "Fuel-efficient routing with backhauls prepped before you unload.", span: "md:col-span-3" },
  { icon: ClipboardCheck, title: "Paperwork &amp; Billing", desc: "Rate cons, BOLs, invoices — we chase brokers so you don&rsquo;t.", span: "md:col-span-3" },
  { icon: Radio, title: "Real-Time Comms", desc: "One text or call reaches driver, broker, and shipper in the loop.", span: "md:col-span-3" },
];

const FREIGHT_IMG = "https://images.unsplash.com/photo-1761133381018-aed5063d22fe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHxzZW1pJTIwdHJ1Y2slMjBoaWdod2F5fGVufDB8fHx8MTc4MTAzMjcyNnww&ixlib=rb-4.1.0&q=85";

export default function Services() {
  return (
    <section id="services" className="bg-white py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <Reveal>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-12">
            <div className="md:col-span-7">
              <p className="font-mono-label text-xs text-[#FF4500]" data-testid="services-eyebrow">CHAPTER 02 / WHAT WE DO</p>
              <h2 className="font-display font-bold text-4xl md:text-6xl mt-4 leading-[1]" data-testid="services-headline">
                Full-service<br/>dispatching. <span className="italic">Period.</span>
              </h2>
            </div>
            <p className="md:col-span-5 text-[#495057] text-base leading-relaxed">
              From the first load you book with us to the last invoice we collect, one dispatcher owns your truck&rsquo;s day. No portals, no ping-pong.
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-12 md:grid-rows-2 gap-px bg-black/10 border border-black/10">
          <motion.div
            initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-10% 0px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-6 md:row-span-2 relative bg-white overflow-hidden min-h-[420px] group" data-testid="service-card-featured">
            <img src={FREIGHT_IMG} alt="Truck cruising down an American highway" className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-[1500ms] ease-out"/>
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            <div className="relative h-full p-10 flex flex-col justify-end text-white">
              <Search size={32} strokeWidth={1.5} className="mb-6 text-[#FF4500]"/>
              <h3 className="font-display font-semibold text-3xl md:text-5xl leading-tight">Load<br/>Sourcing.</h3>
              <p className="mt-4 text-white/85 max-w-md leading-relaxed">
                Every morning we&rsquo;re on DAT, Truckstop, and direct broker phones — filtering for RPM,
                credit score, and lanes that keep you close to home when you need to be.
              </p>
              <a href="#quote" className="font-mono-label text-xs mt-6 inline-flex items-center gap-2 text-[#FF4500]" data-testid="service-cta-sourcing">GET DISPATCHED →</a>
            </div>
          </motion.div>

          {services.slice(1).map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-10% 0px" }}
              transition={{ duration: 0.7, delay: 0.1 + i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className={`bg-white p-8 ${s.span} flex flex-col group hover:bg-[#F8F9FA] transition-colors duration-500`}
              data-testid={`service-card-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <s.icon size={28} strokeWidth={1.5} className="text-black group-hover:text-[#FF4500] transition-colors" />
              <h3 className="font-display font-semibold text-xl mt-6" dangerouslySetInnerHTML={{ __html: s.title }} />
              <p className="text-sm text-[#495057] mt-3 leading-relaxed flex-1" dangerouslySetInnerHTML={{ __html: s.desc }} />
              <p className="font-mono-label text-[10px] text-black/40 mt-6">0{i + 2}</p>
            </motion.div>
          ))}
        </div>

        {/* Fee band */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="mt-8 border border-black bg-white grid grid-cols-1 md:grid-cols-3 gap-px bg-black/10">
          <div className="bg-white p-6 flex items-center gap-4">
            <Wallet size={22} className="text-[#FF4500]"/>
            <div>
              <p className="font-mono-label text-[10px] text-black/50">DISPATCH FEE</p>
              <p className="font-display font-semibold text-xl">5–7% of gross</p>
            </div>
          </div>
          <div className="bg-white p-6">
            <p className="font-mono-label text-[10px] text-black/50">NO CONTRACT</p>
            <p className="font-display font-semibold text-xl">Cancel anytime</p>
          </div>
          <div className="bg-white p-6">
            <p className="font-mono-label text-[10px] text-black/50">PAYOUT</p>
            <p className="font-display font-semibold text-xl">Direct to your factor</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
